import { useFeatureValue } from '@growthbook/growthbook-react';
import { Box, Typography } from '@mui/material';
import { useLocation } from 'react-router-dom';

import { CardFlair, CardFlairVariants } from '@/components';
import { ComplianceTypes } from '@/constants';
import {
  selectActiveEpisodeId,
  selectEpisodeCompliance,
  useAppSelector,
} from '@/store';
import { appStyles } from '@/theme';
import { Episode } from '@/types';
import { convertSecondsToMinutes } from '@/utils';

import { EpisodeCoverImageTrailerPreview } from '../EpisodeCoverImageTrailerPreview';

export const EpisodeCoverImage = ({ episode }: { episode: Episode }) => {
  const isTrailerPreviewEnabled = useFeatureValue(
    'logged-out-trailer-previews-episode-details',
    false,
  );
  const location = useLocation();

  const compliance = useAppSelector(state =>
    selectEpisodeCompliance(state, episode?.episodeId),
  );
  const activeEpisodeId = useAppSelector(selectActiveEpisodeId);

  const episodeDuration = convertSecondsToMinutes(episode.duration);
  const queryParams = Object.fromEntries(
    new URLSearchParams(location.search).entries(),
  );
  const isFromNewsletter = queryParams.newsletter === 'true';

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        borderRadius: 4,
        overflow: 'hidden',
        aspectRatio: 480 / 312,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}>
      <Box
        component="img"
        src={episode.cover}
        alt="episode cover"
        sx={{ height: 'auto', width: '100%' }}
      />
      {episode.episodeAgeTag === 'new' && (
        <CardFlair variant={CardFlairVariants.LARGE} />
      )}
      {!!episode.companyLogo && (
        <Box
          component="img"
          src={episode.companyLogo}
          alt="company logo"
          sx={{
            position: 'absolute',
            top: 16,
            left: 16,
            borderRadius: '8px',
            boxShadow: '0px 4px 16px 0px rgba(0, 0, 0, 0.35)',
            height: 54,
            width: 54,
          }}
        />
      )}
      <Box
        sx={{
          position: 'absolute',
          left: 16,
          bottom: 16,
          borderRadius: '8px',
          backgroundColor: 'white',
          boxShadow: '0px 4px 16px 0px rgba(0, 0, 0, 0.3)',
          opacity: 0.98,
          px: 1.5,
          display: episode.episodeId ? undefined : 'none', // CPA Canda does not have credit info
        }}>
        {compliance === ComplianceTypes.CPE ? (
          <Box
            sx={{
              display: 'flex',
              gap: 1.25,
              alignItems: 'center',
              justifyContent: 'center',
            }}>
            <Typography
              variant="hero4"
              color="accent.main"
              sx={appStyles.gradientText}>
              {episode.CPEDetails?.credits}
            </Typography>
            <Box>
              <Typography variant="body3SemiBold">CPE Credits</Typography>
              <Typography variant="body3SemiBold">
                {episode.CPEDetails?.fieldOfStudy}
              </Typography>
            </Box>
          </Box>
        ) : (
          <Box
            sx={{
              display: 'flex',
              gap: 1.25,
              alignItems: 'center',
              justifyContent: 'center',
            }}>
            <Typography
              variant="hero4"
              color="accent.main"
              sx={appStyles.gradientText}>
              {episodeDuration}
            </Typography>
            <Typography variant="body3">
              Mins <br />
              CPD
            </Typography>
          </Box>
        )}
      </Box>
      {isTrailerPreviewEnabled &&
        isFromNewsletter &&
        !!episode.trailerPreviewUrl &&
        activeEpisodeId !== episode.episodeId && (
          <EpisodeCoverImageTrailerPreview episode={episode} />
        )}
    </Box>
  );
};
